import Component from '@glimmer/component';
import Table from 'tio-reimagined/components/table';
import Paginator from 'tio-reimagined/components/paginator';
import type { ProgramsSearchResult, AggregatedProgramInstance } from 'tio-common/models/search';
import type { FieldOption } from '../../utils/opensearch-field-mapper';
import { generateSmartLabel } from '../../utils/label-generation';
import { snakeToCamelCase } from 'tio-common/utils/string-case-conversion';
import { DEFAULT_COLUMNS, getColumnConfig } from '../../utils/tas-column-config';
import { formatAsDollars } from 'tio-common/utils/format';
import { employeeCustomFieldValue } from '../../utils/employee-custom-field-resolver';
import { tasCustomFieldValue } from '../../utils/tas-custom-field-resolver';

// Application state constants from original implementation
const IN_DRAFT = [
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.COURSES_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
];

const IN_PROGRESS = [
  'TAS.ApplicationState.ATTEND',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
  'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
];

const IN_COMPLETED = ['TAS.ApplicationState.FULFILLED', 'TAS.ApplicationState.ABANDONED'];

export interface ProgramsAggregateTableSignature {
  Args: {
    results: ProgramsSearchResult;
    isLoading: boolean;

    // Pagination
    limit: number;
    page: number;

    // Column selection
    selectedColumns?: string[];
    availableFields?: FieldOption[];

    // Filter data for empty state
    selectedFilters?: Record<string, string | null>;

    // Callbacks
    onPageChange: (page: number) => void;
  };
  Element: HTMLDivElement;
}

export default class ProgramsAggregateTableComponent extends Component<ProgramsAggregateTableSignature> {
  private readonly hiddenObjectColumnKeys = new Set([
    'employeeEfFields.employeeCustomFields',
    'employeeEfFields.employeeFields',
  ]);

  private isHiddenObjectColumn(columnKey: string): boolean {
    return this.hiddenObjectColumnKeys.has(columnKey);
  }

  /**
   * Build columns list based on selected columns or fall back to defaults.
   * Uses shared column configuration for consistency with individual table.
   */
  get columns() {
    // Aggregation columns that should ALWAYS be included in aggregate view
    const aggregationColumnKeys = [
      'total_applications',
      'in_draft',
      'in_progress',
      'completed',
    ].map((key) => snakeToCamelCase(key));

    // Use default columns from shared config if no selection provided
    // Convert snake_case keys to camelCase for internal use
    const defaultColumnKeys = DEFAULT_COLUMNS.aggregate.map((key) =>
      key.includes('_') ? snakeToCamelCase(key) : key
    );

    // Determine which columns to display
    let columnsToDisplay: string[];
    if (this.args.selectedColumns && this.args.selectedColumns.length > 0) {
      // User has selected columns - use those for data fields but always append aggregation columns
      const selectedDataColumns = this.args.selectedColumns
        .map((key) => (key.includes('_') ? snakeToCamelCase(key) : key))
        .filter((key) => !this.isHiddenObjectColumn(key));
      // Combine selected data columns with aggregation columns (remove duplicates)
      columnsToDisplay = [
        ...selectedDataColumns.filter((col) => !aggregationColumnKeys.includes(col)),
        ...aggregationColumnKeys,
      ];
    } else {
      // No selection - use defaults
      columnsToDisplay = defaultColumnKeys;
    }

    // Build column definitions
    const columns = columnsToDisplay.map((columnKey) => {
      // Convert camelCase to snake_case for config lookup
      // Both TAS_PROGRAMS_COLUMN_MAPPING and AGGREGATION_COLUMNS use snake_case keys
      const snakeKey = columnKey.replace(/([A-Z])/g, (letter) => `_${letter.toLowerCase()}`);
      const config = getColumnConfig(snakeKey);

      if (config) {
        return {
          key: columnKey,
          label: config.label,
          type: config.type,
        };
      }

      // Fallback: look up in availableFields, then auto-generate
      return {
        key: columnKey,
        label: generateSmartLabel(columnKey, { availableFields: this.args.availableFields }),
        type: 'text' as const,
      };
    });

    return columns;
  }

  // Get application state count using the correct aggregate structure
  getApplicationStateCount = (
    applicationStateCounts: Record<string, number> | undefined,
    stateKeyOrKeys: string | string[]
  ): number => {
    if (!applicationStateCounts || typeof applicationStateCounts !== 'object') {
      return 0;
    }

    if (Array.isArray(stateKeyOrKeys)) {
      // sum up each individual lookup
      return stateKeyOrKeys.reduce(
        (total, key) => total + this.getApplicationStateCount(applicationStateCounts, key),
        0
      );
    }

    // The data is already in Record<string, number> format after transformation
    return applicationStateCounts[stateKeyOrKeys] || 0;
  };

  // Get cell value from aggregated item (already transformed by data provider)
  getCellValue = (item: unknown, columnKey: string): unknown => {
    const typedItem = item as AggregatedProgramInstance & Record<string, unknown>;

    // Handle special aggregation columns first
    // Note: columnKey is in camelCase format at this point
    switch (columnKey) {
      case 'totalApplications': {
        // Calculate total as sum of all application state counts (not raw document count)
        const stateCounts = typedItem.applicationStateCounts || {};
        return Object.values(stateCounts).reduce((total, count) => {
          const numTotal = typeof total === 'number' ? total : 0;
          const numCount = typeof count === 'number' ? count : 0;
          return numTotal + numCount;
        }, 0);
      }
      case 'inDraft':
        return this.getApplicationStateCount(typedItem.applicationStateCounts, IN_DRAFT);
      case 'inProgress':
        return this.getApplicationStateCount(typedItem.applicationStateCounts, IN_PROGRESS);
      case 'completed':
        return this.getApplicationStateCount(typedItem.applicationStateCounts, IN_COMPLETED);
      default: {
        // For all other columns, use the column key directly to access properties
        // The columnKey is already in camelCase format matching the aggregated data
        let value: unknown = typedItem[columnKey as keyof AggregatedProgramInstance];

        if (value === undefined && columnKey.includes('.')) {
          value = columnKey.split('.').reduce<unknown>((acc, part) => {
            if (!acc || typeof acc !== 'object') {
              return undefined;
            }

            return (acc as Record<string, unknown>)[part];
          }, typedItem);

          // Backward-compatibility for Employee EF custom field keys where
          // values are nested under employeeEfFields.employeeCustomFields.
          // Treat empty-string placeholders as missing so fallback lookup still runs.
          if (
            (value === undefined || value === null || value === '') &&
            columnKey.startsWith('employeeEfFields.') &&
            !columnKey.startsWith('employeeEfFields.employeeCustomFields.')
          ) {
            const leaf = columnKey.split('.').filter(Boolean).at(-1);
            if (leaf) {
              const customValue = (
                typedItem.employeeEfFields as Record<string, unknown> | undefined
              )?.employeeCustomFields as Record<string, unknown> | undefined;
              value = employeeCustomFieldValue(customValue, leaf);
            }
          }

          // Course object fields are stored as a nested array (one entry per course).
          // Collect and comma-join the leaf value across all courses.
          if (
            (value === undefined || value === null || value === '') &&
            columnKey.startsWith('tasApplicationCoursesObject.')
          ) {
            const courses = (typedItem as Record<string, unknown>)['tasApplicationCoursesObject'];
            if (Array.isArray(courses)) {
              const leafKey = columnKey.slice('tasApplicationCoursesObject.'.length);
              const values = (courses as Array<Record<string, unknown>>)
                .map((course) => course[leafKey])
                .filter((v) => v !== undefined && v !== null && v !== '');
              if (values.length > 0) {
                value = values.join(', ');
              }
            }
          }

          // Some OpenSearch documents store flattened TAS custom-field values under
          // legacy nested keys inside the flattened container object, e.g.:
          // tasProgramInstanceCustomFieldsFlattened: {
          //   "tasProgramInstanceCustomFields.tasProgramInstanceCustomFieldTasProgramInstance2": "..."
          // }
          // If the direct lookup misses, perform a normalized key match within the container.
          if (value === undefined || value === null || value === '') {
            const customValue = tasCustomFieldValue(
              typedItem as Record<string, unknown>,
              columnKey
            );
            if (customValue !== undefined) {
              value = customValue;
            }
          }
        }

        return value !== undefined && value !== null ? value : '';
      }
    }
  };

  // Format cell value based on type
  formatCellValue = (value: unknown, type: string): string => {
    const isEmpty = value === null || value === undefined || value === '';
    if (isEmpty) {
      return type === 'currency' ? 'N/A' : '';
    }

    switch (type) {
      case 'currency': {
        const numValue = typeof value === 'number' ? value : Number(value);
        return isNaN(numValue) ? String(value) : formatAsDollars(numValue, { cents: true });
      }
      case 'number':
        return String(value);
      case 'text':
      default:
        return String(value);
    }
  };

  <template>
    <div ...attributes>
      <Table @isLoading={{@isLoading}}>
        <:thead as |options|>
          <options.tr>
            {{#each this.columns as |column|}}
              <options.th>{{column.label}}</options.th>
            {{/each}}
          </options.tr>
        </:thead>
        <:tbody as |options|>
          {{! @glint-expect-error: search results can have different types }}
          {{#each @results.items as |item|}}
            <options.tr class="align-top">
              {{#each this.columns as |column|}}
                <options.td>
                  {{this.formatCellValue (this.getCellValue item column.key) column.type}}
                </options.td>
              {{/each}}
            </options.tr>
          {{else}}
            <options.tr>
              <options.td colspan="{{this.columns.length}}">
                <div class="text-sm text-gray-700 dark:text-gray-300">
                  <p>You have searched for aggregated program reports with the following criteria
                    and found no results:</p>
                  {{#if @selectedFilters}}
                    <ul class="list-disc pl-5 flex flex-col gap-y-1">
                      {{#each-in @selectedFilters as |key value|}}
                        {{#if value}}
                          <li>{{key}}: {{value}}</li>
                        {{/if}}
                      {{/each-in}}
                    </ul>
                  {{/if}}
                </div>
              </options.td>
            </options.tr>
          {{/each}}
        </:tbody>
        <:tfoot as |options|>
          <options.tr>
            <td colspan="{{this.columns.length}}">
              <Paginator
                @totalItems={{@results.total}}
                @pageSize={{@limit}}
                @currentPage={{@page}}
                @onChange={{@onPageChange}}
              />
            </td>
          </options.tr>
        </:tfoot>
      </Table>
    </div>
  </template>
}
